// 2026-07-25:/analytics 页面 — Demo → Real GA4。
//
// レイアウト・コンポーネント階層は 元の mock 実装と 完全 同形。
// データソースのみ mock.ts → ga4Api.getReport(period) に 置換。
//
// State machine:
// - loading / ready / error / not_configured(GA4 未連携)
// - period 切替 → 再 fetch
//
// Conversion フィールド(キーイベント):
// - Property にキーイベントが未設定の場合、kpis.conversions = 0 と なる(GA4 が返す)。
//   UI 上 は "0" / "未設定" を 表示。エラーで ブロックしない。

import { useEffect, useMemo, useState } from "react";
import {
  Users, Eye, Clock, MousePointerClick, Target, Download,
  Sparkles, MessageCircle, Send, X, BarChart3, LayoutList, Share2, MonitorSmartphone,
  MapPin, UserPlus,
} from "lucide-react";
import { Card } from "../../components/ui/Card";
import { StatCard } from "../../components/ui/StatCard";
import { Button } from "../../components/ui/Button";
import { MockBars, MockDonut } from "../../components/ui/MockChart";
import { ApiError } from "../../api/client";
import { ga4Api, type AiInsightData, type Ga4Period, type Ga4Report } from "../../api/ga4";

const PERIODS: { value: Ga4Period; label: string }[] = [
  { value: "today", label: "今日" },
  { value: "yesterday", label: "昨日" },
  { value: "7d", label: "7日間" },
  { value: "30d", label: "30日間" },
  { value: "thismonth", label: "今月" },
  { value: "prevmonth", label: "前月" },
];

const KPI_ICONS = [
  <Users size={16} />,            // totalUsers
  <Eye size={16} />,              // screenPageViews
  <Clock size={16} />,            // averageSessionDuration
  <MousePointerClick size={16} />,// bounceRate
  <Target size={16} />,           // conversions
];

type ChartView = "trend" | "sources" | "device";
type AudienceView = "popular" | "new" | "city" | "device";

const CHART_VIEWS: { key: ChartView; label: string; icon: JSX.Element }[] = [
  { key: "trend", label: "推移", icon: <BarChart3 size={14} /> },
  { key: "sources", label: "流入", icon: <Share2 size={14} /> },
  { key: "device", label: "行動", icon: <MonitorSmartphone size={14} /> },
];

const AUDIENCE_VIEWS: { key: AudienceView; label: string; icon: JSX.Element }[] = [
  { key: "popular", label: "人気ページ", icon: <LayoutList size={14} /> },
  { key: "new", label: "新規", icon: <UserPlus size={14} /> },
  { key: "city", label: "市区町村", icon: <MapPin size={14} /> },
  { key: "device", label: "デバイス", icon: <MonitorSmartphone size={14} /> },
];

function isChartView(value: string | null): value is ChartView {
  return value === "trend" || value === "sources" || value === "device";
}

function isAudienceView(value: string | null): value is AudienceView {
  return value === "popular" || value === "new" || value === "city" || value === "device";
}

function readStoredChartView(): ChartView {
  if (typeof window === "undefined") return "trend";
  const value = window.localStorage.getItem("analytics.chartView");
  return isChartView(value) ? value : "trend";
}

function readStoredAudienceView(): AudienceView {
  if (typeof window === "undefined") return "popular";
  const value = window.localStorage.getItem("analytics.audienceView");
  return isAudienceView(value) ? value : "popular";
}

function buildPageHref(path?: string, hostName?: string): string | null {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  const cleanHost = (hostName || "").trim();
  if (cleanHost && cleanHost !== "(not set)") {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    return `https://${cleanHost}${normalizedPath}`;
  }
  if (typeof window === "undefined") return path;
  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return new URL(normalizedPath, window.location.origin).toString();
}

function formatSeconds(seconds: number): string {
  if (!seconds || seconds < 0) return "0:00";
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function formatBouncePercent(ratio: number): string {
  if (!ratio && ratio !== 0) return "0%";
  return `${(ratio * 100).toFixed(1)}%`;
}

function formatChange(value: number | null | undefined, suffix = "%"): string {
  if (typeof value !== "number") return "前期なし";
  return `${value >= 0 ? "+" : ""}${value}${suffix}`;
}

function priorityLabel(priority?: string): string {
  if (priority === "high") return "高";
  if (priority === "low") return "低";
  return "中";
}

function normalizeQuestion(text: string): string {
  return text.trim().slice(0, 500);
}

function ga4InsightCacheKey(period: Ga4Period): string {
  return `analytics.aiInsight.${period}`;
}

function formatShortDate(dateText?: string): string {
  if (!dateText) return "";
  const date = new Date(`${dateText}T00:00:00`);
  if (Number.isNaN(date.getTime())) return dateText;
  return `${date.getMonth() + 1}/${date.getDate()}`;
}

function midpointDate(startText?: string, endText?: string): string {
  if (!startText || !endText) return "";
  const start = new Date(`${startText}T00:00:00`);
  const end = new Date(`${endText}T00:00:00`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return "";
  return formatShortDate(new Date((start.getTime() + end.getTime()) / 2).toISOString().slice(0, 10));
}

function AnalyticsBarTrend({
  points,
  labels,
  color,
}: {
  points: number[];
  labels: string[];
  color: string;
}) {
  const max = Math.max(...points, 1);
  const ticks = max <= 5
    ? Array.from({ length: max + 1 }, (_, index) => max - index)
    : [max, Math.floor(max / 2), 0];
  return (
    <div className="analytics-axis-chart analytics-axis-chart--bars">
      <div className="analytics-y-axis" aria-hidden="true">
        {ticks.map((tick) => (
          <span key={tick} style={{ bottom: `${(tick / max) * 100}%` }}>{tick}</span>
        ))}
      </div>
      <div className="analytics-chart-with-x-axis">
        <div className="analytics-bar-chart">
          {points.map((value, index) => (
            <div
              key={index}
              className="analytics-trend-bar"
              style={{ height: `${8 + (Number(value || 0) / max) * 258}px`, background: color }}
              title={`${labels[index] || index + 1}: ${value}`}
            />
          ))}
        </div>
        <div className="analytics-x-axis" aria-hidden="true">
          {labels.map((label, index) => <span key={`${label}-${index}`}>{label}</span>)}
        </div>
      </div>
    </div>
  );
}

function buildLocalSummary(report: Ga4Report | null): string {
  if (!report) return "GA4 データを取得すると、ここに概要が表示されます。";
  const k = report.kpis;
  const topPage = report.pages[0];
  const topSource = report.sources[0];
  const parts = [
    `ユーザーは ${k.totalUsers.toLocaleString()}、PV は ${k.screenPageViews.toLocaleString()} です。`,
    `平均セッションは ${formatSeconds(k.averageSessionDuration)}、直帰率は ${formatBouncePercent(k.bounceRate)} です。`,
  ];
  if (topPage) {
    parts.push(`もっとも見られているページは「${topPage.title || topPage.path}」で、PV は ${topPage.pv.toLocaleString()} です。`);
  }
  if (topSource) {
    parts.push(`主な流入は「${topSource.label}」です。`);
  }
  return parts.join("\n");
}

function renderInlineMarkdown(text: string): React.ReactNode[] {
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return parts.map((part, index) => {
    if (part.startsWith("**") && part.endsWith("**")) {
      return <strong key={index}>{part.slice(2, -2)}</strong>;
    }
    return <span key={index}>{part}</span>;
  });
}

function MarkdownLite({ text }: { text: string }) {
  const lines = text.split(/\r?\n/);
  return (
    <div className="analytics-markdown-lite">
      {lines.map((line, index) => {
        const trimmed = line.trim();
        if (!trimmed) {
          return <div key={index} className="analytics-markdown-spacer" />;
        }
        if (trimmed.startsWith("- ")) {
          return (
            <div key={index} className="analytics-markdown-bullet">
              <span aria-hidden="true">•</span>
              <p>{renderInlineMarkdown(trimmed.slice(2))}</p>
            </div>
          );
        }
        return <p key={index}>{renderInlineMarkdown(trimmed)}</p>;
      })}
    </div>
  );
}

type Status = "loading" | "ready" | "error" | "not_configured" | "empty";
type AiChatMessage = {
  id: string;
  role: "user" | "assistant";
  text: string;
};

type ActionDefinition = {
  title: string;
  description: string;
};

const GA4_ACTIONS: Record<string, ActionDefinition> = {
  improve_cta: { title: "CTAを見直す", description: "問い合わせ・予約・LINEなどの導線を分かりやすくします。" },
  review_top_page: { title: "人気ページを確認", description: "閲覧が多いページの見出しと導線を優先して改善します。" },
  setup_conversion: { title: "CV計測を設定", description: "フォーム送信やクリックをキーイベントとして計測します。" },
  improve_content: { title: "内容を改善", description: "滞在時間や直帰率が弱いページの本文構成を調整します。" },
  check_traffic_source: { title: "流入元を確認", description: "直接流入・検索流入・参照元の偏りを確認します。" },
};

function fallbackInsight(report: Ga4Report | null): AiInsightData {
  const summary = buildLocalSummary(report);
  return {
    summary,
    insights: report ? [
      {
        severity: "neutral",
        icon: "users",
        title: "基本指標を確認",
        description: `ユーザー ${report.kpis.totalUsers.toLocaleString()}、PV ${report.kpis.screenPageViews.toLocaleString()} です。`,
      },
      {
        severity: report.kpis.bounceRate > 0.6 ? "warning" : "neutral",
        icon: "clock",
        title: "滞在と直帰を確認",
        description: `平均セッションは ${formatSeconds(report.kpis.averageSessionDuration)}、直帰率は ${formatBouncePercent(report.kpis.bounceRate)} です。`,
      },
    ] : [],
    actions: [
      { action_id: "review_top_page", priority: "medium" },
      { action_id: "setup_conversion", priority: "medium" },
    ],
    questions: [
      "どのページを優先して改善すべき？",
      "問い合わせを増やすには？",
    ],
  };
}

function insightToText(data: AiInsightData): string {
  const lines: string[] = [];
  if (data.summary) lines.push(data.summary);
  const insights = (data.insights || []).slice(0, 3);
  if (insights.length > 0) {
    lines.push("");
    insights.forEach((item) => {
      lines.push(`- **${item.title}**：${item.description}`);
    });
  }
  return lines.join("\n") || "表示中の指標から、現在の状況を確認できます。";
}

function AiInsightPanel({
  data,
  actionCatalog,
  showChat,
  onChatOpen,
  onQuestionClick,
}: {
  data: AiInsightData;
  actionCatalog: Record<string, ActionDefinition>;
  showChat?: boolean;
  onChatOpen?: () => void;
  onQuestionClick?: (question: string) => void;
}) {
  const insights = (data.insights || []).slice(0, 3);
  const actions = (data.actions || []).slice(0, 4);
  const questions = (data.questions || []).slice(0, 3);
  return (
    <div className="ai-insight-panel">
      {data.summary && <p className="ai-insight-summary">{data.summary}</p>}
      {insights.length > 0 && (
        <div className="ai-insight-section">
          <span className="ai-insight-section-label">主な気づき</span>
          {insights.map((item, index) => (
            <div className={`ai-insight-item ai-insight-item--${item.severity || "neutral"}`} key={`${item.title}-${index}`}>
              <span className="ai-insight-dot" />
              <div>
                <strong>{item.title}</strong>
                <p>{item.description}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {actions.length > 0 && (
        <div className="ai-insight-section">
          <span className="ai-insight-section-label">おすすめアクション</span>
          <div className="ai-insight-actions">
            {actions.map((action) => {
              const definition = actionCatalog[action.action_id] || {
                title: action.action_id,
                description: "この項目を確認してください。",
              };
              return (
                <div className={`ai-insight-action ai-insight-action--${action.priority || "medium"}`} key={action.action_id}>
                  <strong>{definition.title}</strong>
                  <p>{definition.description}</p>
                  <span>{priorityLabel(action.priority)}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {questions.length > 0 && (
        <div className="analytics-ai-suggestions">
          {questions.map((q) => (
            <button key={q} type="button" onClick={() => onQuestionClick?.(q)}>
              {q}
            </button>
          ))}
        </div>
      )}
      {showChat && onChatOpen && (
        <button className="ai-insight-inline-chat" type="button" onClick={onChatOpen}>
          <MessageCircle size={14} />
          チャットで詳しく聞く
        </button>
      )}
    </div>
  );
}

export const AnalyticsPage = () => {
  const [period, setPeriod] = useState<Ga4Period>("30d");
  const [status, setStatus] = useState<Status>("loading");
  const [errorCode, setErrorCode] = useState<string>("");
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [report, setReport] = useState<Ga4Report | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [chartView, setChartView] = useState<ChartView>(() => readStoredChartView());
  const [audienceView, setAudienceView] = useState<AudienceView>(() => readStoredAudienceView());
  const [aiModalOpen, setAiModalOpen] = useState(false);
  const [aiQuestion, setAiQuestion] = useState("");
  const [aiInsight, setAiInsight] = useState<AiInsightData | null>(null);
  const [aiSummarySource, setAiSummarySource] = useState<"ai" | "fallback">("fallback");
  const [aiInsightLoading, setAiInsightLoading] = useState(false);
  const [aiMessages, setAiMessages] = useState<AiChatMessage[]>([]);
  const [aiBusy, setAiBusy] = useState(false);
  const [aiError, setAiError] = useState("");

  const setReportState = useMemo(
    () => (r: Ga4Report) => {
      setReport(r);
      const hasAny =
        r.kpis.totalUsers > 0 ||
        r.kpis.screenPageViews > 0 ||
        r.trend.length > 0 ||
        r.sources.length > 0 ||
        r.pages.length > 0;
      setStatus(hasAny ? "ready" : "empty");
    },
    [],
  );

  const fetchReport = useMemo(
    () => async (p: Ga4Period, options?: { refresh?: boolean; useCache?: boolean }) => {
      setStatus("loading");
      setErrorCode("");
      setErrorMessage("");
      try {
        if (options?.refresh) {
          await ga4Api.refreshReport(p, true);
        }
        const r = await ga4Api.getReport(p);
        setReportState(r);
      } catch (e) {
        if (e instanceof ApiError && (e.code === "AUTH_GA4_NOT_CONFIGURED" || e.status === 404)) {
          setStatus("not_configured");
        } else if (e instanceof ApiError) {
          setStatus("error");
          setErrorCode(e.code);
          setErrorMessage(e.message);
        } else {
          setStatus("error");
          setErrorMessage(e instanceof Error ? e.message : String(e));
        }
      }
    },
    [setReportState],
  );

  useEffect(() => {
    void fetchReport(period);
  }, [period, fetchReport]);

  useEffect(() => {
    window.localStorage.setItem("analytics.chartView", chartView);
  }, [chartView]);

  useEffect(() => {
    window.localStorage.setItem("analytics.audienceView", audienceView);
  }, [audienceView]);

  const kpis = report?.kpis;
  const comparisonLabel = report?.comparison?.label ?? "前期比";
  const trend = report?.trend ?? [];
  const metricTrends = report?.metricTrends ?? {
    totalUsers: [],
    screenPageViews: [],
    averageSessionDuration: [],
    bounceRate: [],
    conversions: [],
  };
  const sources = report?.sources ?? [];
  const pages = report?.pages ?? [];
  const audience = report?.audience ?? { newUsersTrend: [], countries: [], cities: [], devices: [] };
  const newUsersTrend = audience.newUsersTrend ?? [];
  const trendDateLabels = [
    formatShortDate(report?.comparison?.current.startDate),
    midpointDate(report?.comparison?.current.startDate, report?.comparison?.current.endDate),
    formatShortDate(report?.comparison?.current.endDate),
  ];
  const sourceBars = useMemo(() => {
    const max = Math.max(...sources.map((s) => s.value), 1);
    return sources.map((s) => ({
      label: s.label,
      value: s.value,
      max,
      color: s.color,
    }));
  }, [sources]);
  const actionRows = useMemo(() => {
    if (!kpis) return [];
    return [
      { label: "目標達成", value: kpis.conversions, max: Math.max(kpis.conversions, 1), color: "#2563eb" },
      { label: "離脱リスク", value: Math.round(kpis.bounceRate * 100), max: 100, color: "#f59e0b" },
      { label: "熟読度", value: Math.min(Math.round(kpis.averageSessionDuration), 180), max: 180, color: "#16a34a" },
    ];
  }, [kpis]);

  const suggestedQuestions = useMemo(() => [
    "なぜPVが伸びていますか？",
    "問い合わせを増やすには？",
    "どのページを改善すべき？",
  ], []);

  useEffect(() => {
    if (!report || status !== "ready") return;
    let cancelled = false;
    setAiInsight(fallbackInsight(report));
    setAiSummarySource("fallback");
    setAiInsightLoading(true);
    void (async () => {
      try {
        const result = await ga4Api.generateAiInsight({ period });
        if (cancelled) return;
        if (result.insight) {
          setAiInsight(result.insight);
          setAiSummarySource("ai");
        }
      } catch {
        if (!cancelled) {
          setAiInsight(fallbackInsight(report));
          setAiSummarySource("fallback");
        }
      } finally {
        if (!cancelled) setAiInsightLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [period, report, status]);

  const askAi = async (question?: string) => {
    const q = normalizeQuestion(question ?? aiQuestion);
    if (!q || aiBusy) return;
    const userMessage: AiChatMessage = { id: `${Date.now()}-user`, role: "user", text: q };
    setAiMessages((prev) => [...prev, userMessage]);
    setAiBusy(true);
    setAiError("");
    setAiQuestion("");
    try {
      const result = await ga4Api.generateAiInsight({ period, question: q });
      const assistantMessage: AiChatMessage = {
        id: `${Date.now()}-assistant`,
        role: "assistant",
        text: result.text || "回答を生成できませんでした。",
      };
      setAiMessages((prev) => [...prev, assistantMessage]);
    } catch (e) {
      setAiError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    } finally {
      setAiBusy(false);
    }
  };


  // ---- loading ----
  if (status === "loading") {
    return (
      <div className="analytics-loading">
        <Card>
          <div style={{ padding: 40, textAlign: "center", color: "#6b7280", fontSize: 13 }}>
            GA4 からデータを取得中...
          </div>
        </Card>
      </div>
    );
  }

  // ---- not configured ----
  if (status === "not_configured") {
    return (
      <div>
        <Card>
          <div
            style={{
              padding: "32px",
              textAlign: "center",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              alignItems: "center",
            }}
          >
            <Target size={28} color="#6b7280" />
            <div style={{ fontSize: 14, fontWeight: 600, color: "#111827" }}>
              Google Analytics が連携されていません
            </div>
            <div style={{ fontSize: 12, color: "#6b7280", maxWidth: 420 }}>
              /settings → Google Analytics 設定 から連携を行ってください。
            </div>
          </div>
        </Card>
      </div>
    );
  }

  // ---- error ----
  if (status === "error") {
    return (
      <div>
        <Card>
          <div
            style={{
              padding: "24px",
              background: "#fef2f2",
              color: "#b91c1c",
              borderRadius: 10,
              fontSize: 13,
            }}
          >
            <div style={{ fontWeight: 600, marginBottom: 4 }}>GA4 レポート取得に失敗しました</div>
            <div style={{ fontSize: 12, color: "#7f1d1d" }}>
              {errorCode ? `[${errorCode}] ` : ""}{errorMessage}
            </div>
            <button
              onClick={() => void (async () => {
                setRefreshing(true);
                try {
                  window.sessionStorage.removeItem(ga4InsightCacheKey(period));
                  await fetchReport(period, { refresh: true, useCache: false });
                } finally {
                  setRefreshing(false);
                }
              })()}
              style={{
                marginTop: 10,
                padding: "6px 12px",
                background: "#fff",
                border: "1px solid #b91c1c",
                color: "#b91c1c",
                borderRadius: 6,
                fontSize: 12,
                cursor: "pointer",
              }}
            >
              {refreshing ? "更新中..." : "今すぐ更新"}
            </button>
          </div>
        </Card>
      </div>
    );
  }

  // ---- ready / empty ----
  // StatCard 用の5 个 KPI(順序 = mock と同形)
  const statCardsData = kpis ? [
    { title: "ユニークユーザー", value: kpis.totalUsers.toLocaleString(), change: kpis.change.totalUsers, trend: metricTrends.totalUsers.length > 0 ? metricTrends.totalUsers : [0], color: "#2563eb" },
    { title: "ページビュー", value: kpis.screenPageViews.toLocaleString(), change: kpis.change.screenPageViews, trend: metricTrends.screenPageViews.length > 0 ? metricTrends.screenPageViews : [0], color: "#06b6d4" },
    { title: "平均セッション", value: formatSeconds(kpis.averageSessionDuration), change: kpis.change.averageSessionDuration, trend: metricTrends.averageSessionDuration.length > 0 ? metricTrends.averageSessionDuration : [0], color: "#16a34a" },
    { title: "直帰率", value: formatBouncePercent(kpis.bounceRate), change: kpis.change.bounceRate, trend: metricTrends.bounceRate.length > 0 ? metricTrends.bounceRate : [0], color: "#f59e0b" },
    { title: "コンバージョン", value: kpis.conversions.toLocaleString(), change: kpis.change.conversions, trend: metricTrends.conversions.length > 0 ? metricTrends.conversions : [0], color: "#ef4444" },
  ] : [];

  return (
    <div className="analytics-page analytics-page--compact">
      <Card className="analytics-toolbar">
        <div className="row-between">
          <div className="row gap-8 analytics-periods">
            {PERIODS.map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`analytics-pill ${period === p.value ? "is-active" : ""}`}
              >
                {p.label}
              </button>
            ))}
          </div>
          <div className="row gap-8">
            <Button variant="secondary" iconLeft={<Download size={14} />} disabled>
              CSVダウンロード
            </Button>
            <Button
              onClick={() => void (async () => {
                setRefreshing(true);
                try {
                  window.sessionStorage.removeItem(ga4InsightCacheKey(period));
                  await fetchReport(period, { refresh: true, useCache: false });
                } finally {
                  setRefreshing(false);
                }
              })()}
              disabled={refreshing}
            >
              {refreshing ? "更新中..." : "今すぐ更新"}
            </Button>
          </div>
        </div>
      </Card>

      <div className="analytics-stats analytics-stats--compact">
        {statCardsData.map((s, i) => (
          <StatCard
            key={s.title}
            title={s.title}
            value={s.value}
            change={s.change}
            changeLabel={comparisonLabel}
            trend={s.trend}
            trendColor={s.color}
            icon={KPI_ICONS[i]}
            small
          />
        ))}
      </div>

      <div className="analytics-main-grid">
        <Card
          className="analytics-focus-card"
          bodyClassName="analytics-panel-body"
          title="サイト分析"
          subtitle={`${PERIODS.find((p) => p.value === period)?.label ?? period} / ${comparisonLabel}`}
          actions={
            <div className="analytics-view-tabs" role="tablist" aria-label="分析表示切替">
              {CHART_VIEWS.map((view) => (
                <button
                  key={view.key}
                  type="button"
                  className={`analytics-view-tab ${chartView === view.key ? "is-active" : ""}`}
                  onClick={() => setChartView(view.key)}
                >
                  {view.icon}
                  {view.label}
                </button>
              ))}
            </div>
          }
        >
          <div className="analytics-focus-body">
            {chartView === "trend" && (
              <div className="analytics-chart-panel">
                <div className="analytics-chart-summary">
                  <span>セッション推移</span>
                  <strong>{trend.reduce((sum, value) => sum + value, 0).toLocaleString()}</strong>
                  <small>{formatChange(kpis?.change.totalUsers)} / ユーザー</small>
                </div>
                {trend.length > 0 ? (
                  <AnalyticsBarTrend points={trend} labels={trendDateLabels} color="#2563eb" />
                ) : (
                  <div className="empty-hint">データがありません</div>
                )}
              </div>
            )}
            {chartView === "sources" && (
              <div className="analytics-split-panel analytics-split-panel--donut">
                {sources.length > 0 ? <MockDonut data={sources} size={170} thickness={22} /> : <div className="empty-hint">データがありません</div>}
                <MockBars bars={sourceBars} height={320} />
              </div>
            )}
            {chartView === "device" && (
              <div className="analytics-split-panel">
                <MockBars bars={actionRows} height={320} />
              </div>
            )}
          </div>
        </Card>

        <Card
          className="analytics-audience-card"
          bodyClassName="analytics-panel-body"
          title="ユーザー / ページ"
          subtitle="表示したい切り口を切替"
          actions={
            <div className="analytics-view-tabs analytics-view-tabs--wrap" role="tablist" aria-label="ユーザー表示切替">
              {AUDIENCE_VIEWS.map((view) => (
                <button
                  key={view.key}
                  type="button"
                  className={`analytics-view-tab ${audienceView === view.key ? "is-active" : ""}`}
                  onClick={() => setAudienceView(view.key)}
                >
                  {view.icon}
                  {view.label}
                </button>
              ))}
            </div>
          }
        >
          <div className="analytics-audience-body">
            {audienceView === "popular" && (
              <div className="analytics-rank-list">
                {pages.slice(0, 6).map((page, index) => {
                  const href = buildPageHref(page.path, page.hostName);
                  const content = (
                    <>
                      <div className="analytics-rank-index">{index + 1}</div>
                      <div className="analytics-rank-main">
                        <strong>{page.title || page.path || "ページ"}</strong>
                        <span>{page.path || "パスなし"}</span>
                      </div>
                      <div className="analytics-rank-values">
                        <strong>{page.pv.toLocaleString()}</strong>
                        <span>PV</span>
                      </div>
                    </>
                  );
                  return href ? (
                    <a
                      className="analytics-rank-row analytics-rank-row--link"
                      href={href}
                      key={`${page.path}-${index}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {content}
                    </a>
                  ) : (
                    <div className="analytics-rank-row" key={`${page.path}-${index}`}>
                      {content}
                    </div>
                  );
                })}
                {pages.length === 0 && <div className="empty-hint">データがありません</div>}
              </div>
            )}
            {audienceView === "new" && (
              <div className="analytics-chart-panel">
                <div className="analytics-chart-summary">
                  <span>新規ユーザー</span>
                  <strong>{newUsersTrend.reduce((sum, value) => sum + value, 0).toLocaleString()}</strong>
                  <small>{PERIODS.find((p) => p.value === period)?.label ?? period}</small>
                </div>
                {newUsersTrend.length > 0 ? (
                  <AnalyticsBarTrend points={newUsersTrend} labels={trendDateLabels} color="#10b981" />
                ) : (
                  <div className="empty-hint">データがありません</div>
                )}
              </div>
            )}
            {audienceView === "city" && (
              audience.cities.length > 0 ? (
                <MockBars bars={audience.cities.map((entry) => ({
                  label: entry.label,
                  value: entry.value,
                  max: Math.max(...audience.cities.map((e) => e.value), 1),
                  color: entry.color,
                }))} height={360} />
              ) : <div className="empty-hint">データがありません</div>
            )}
            {audienceView === "device" && (
              audience.devices.length > 0 ? (
                <div className="analytics-split-panel analytics-split-panel--donut analytics-split-panel--stackable">
                  <MockDonut data={audience.devices} size={170} thickness={22} />
                  <MockBars bars={audience.devices.map((entry) => ({
                    label: entry.label,
                    value: entry.value,
                    max: Math.max(...audience.devices.map((e) => e.value), 1),
                    color: entry.color,
                  }))} height={300} />
                </div>
              ) : <div className="empty-hint">データがありません</div>
            )}
          </div>
        </Card>

        <Card
          className="analytics-ai-card"
          bodyClassName="analytics-panel-body"
          title={<span className="analytics-ai-title"><Sparkles size={18} /> AIインサイト</span>}
          actions={
            <Button variant="secondary" iconLeft={<MessageCircle size={15} />} onClick={() => setAiModalOpen(true)}>
              チャット
            </Button>
          }
        >
          <div className="analytics-ai-notice">表示中の指標だけで回答します。ページ本文は参照しません。</div>
          <div className="analytics-ai-source">{aiSummarySource === "ai" ? "AI要約" : "標準要約"}</div>
          {aiInsightLoading ? (
            <div className="ai-insight-generating" role="status" aria-live="polite">
              <span className="ai-insight-generating-orb" />
              <span>AIがデータを整理しています<span className="ai-insight-generating-dots" /></span>
            </div>
          ) : (
            <AiInsightPanel
              data={aiInsight || fallbackInsight(report)}
              actionCatalog={GA4_ACTIONS}
              onQuestionClick={(q) => { setAiModalOpen(true); void askAi(q); }}
            />
          )}
        </Card>
      </div>

      {aiModalOpen && (
        <div className="analytics-ai-modal-backdrop" role="dialog" aria-modal="true">
          <div className="analytics-ai-modal" onClick={(event) => event.stopPropagation()}>
            <div className="analytics-ai-modal-head">
              <div>
                <h3><Sparkles size={18} /> AIインサイトチャット</h3>
                <p>GA4 の表示可能な数値だけを使って回答します。</p>
              </div>
              <button type="button" onClick={() => setAiModalOpen(false)} aria-label="閉じる"><X size={18} /></button>
            </div>
            <div className="analytics-ai-modal-body">
              <div className="analytics-ai-thread">
                <div className="analytics-ai-message analytics-ai-message--assistant">
                  <MarkdownLite text={insightToText(aiInsight || fallbackInsight(report))} />
                </div>
                {aiMessages.map((message) => (
                  <div
                    key={message.id}
                    className={`analytics-ai-message analytics-ai-message--${message.role}`}
                  >
                    <MarkdownLite text={message.text} />
                  </div>
                ))}
                {aiBusy && (
                  <div className="analytics-ai-message analytics-ai-message--assistant analytics-ai-message--pending">
                    推論中です...
                  </div>
                )}
              </div>
              {aiError && <div className="analytics-ai-error">{aiError}</div>}
              <div className="analytics-ai-suggestions">
                {((aiInsight || fallbackInsight(report)).questions || suggestedQuestions).slice(0, 3).map((q) => (
                  <button key={q} type="button" onClick={() => void askAi(q)} disabled={aiBusy}>
                    {q}
                  </button>
                ))}
              </div>
            </div>
            <div className="analytics-ai-modal-input">
              <input
                value={aiQuestion}
                onChange={(e) => setAiQuestion(e.target.value)}
                placeholder="質問を入力"
                maxLength={500}
              />
              <Button iconLeft={<Send size={15} />} onClick={() => void askAi()} disabled={aiBusy}>
                {aiBusy ? "推論中" : "送信"}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
